import { Component, EventEmitter, Output } from '@angular/core';
import { SpeedDialModule } from 'primeng/speeddial';
import { StyleClassModule } from 'primeng/styleclass';
import { SidebarModule } from 'primeng/sidebar';
import { DataLayerTimelineComponent } from '../data-layer-timeline/data-layer-timeline.component';
import { NgClass } from '@angular/common';
import { WidgetType } from '../../../services/grid-widget-config.service';

interface AdderButton {
  icon: string;
  tooltip?: string;
  type: WidgetType;
}

@Component({
  selector: 'app-widget-adder',
  standalone: true,
  templateUrl: './widget-adder.component.html',
  styleUrl: './widget-adder.component.scss',
  imports: [SpeedDialModule, StyleClassModule, SidebarModule, NgClass],
})
export class WidgetAdderComponent {
  @Output() addWidget = new EventEmitter<WidgetType>();
  showAdder = false;
  hideContent = true; // New flag to control opacity
  items: AdderButton[] = [];

  constructor() {
    this.items = [
      {
        icon: 'fas fa-map-location-dot',
        type: 'map',
        tooltip: 'Karte hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-chart-column',
        type: 'bar',
        tooltip: 'Balken-/ Säulendiagramm hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-chart-line',
        type: 'line',
        tooltip: 'Liniendaigramm hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-chart-pie',
        type: 'pie',
        tooltip: 'Kreisdiagramm hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fa-solid fa-arrow-up-right-dots',
        type: 'scatter',
        tooltip: 'Streudiagramm hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-circle',
        type: 'bubble',
        tooltip: 'Blasendiagramm hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fa-solid fa-satellite-dish',
        type: 'radar',
        tooltip: 'Radardiagramm hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-table',
        type: 'table',
        tooltip: 'Tabelle hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-font',
        type: 'text',
        tooltip: 'Text hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-info-circle',
        type: 'info',
        tooltip: 'Indikatorinfo hinzufügen (Klick oder Drag & Drop)',
      },
      {
        icon: 'fas fa-pager',
        type: 'teaser',
        tooltip: 'Teaser hinzufügen (Klick oder Drag & Drop)',
      },
    ];
  }

  /**
   * A callback function that is triggered when a new widget is added.
   *
   * This function emits an event with the provided widget value, allowing
   * for external handling of the widget addition. Designed to integrate
   * with systems listening for widget addition events.
   *
   * @param {WidgetType} value - The widget data to be added.
   */
  onAddWidget = (value: WidgetType): void => {
    this.addWidget.emit(value);
  };

  /**
   * Toggles the visibility of the adder component. If the adder is currently visible, it triggers the closing process.
   * If the adder is not visible, it triggers the opening process.
   *
   * @return {void} Does not return a value.
   */
  toggleAdder(): void {
    if (this.showAdder) {
      // If hiding, delay opacity until after animation
      this.closeAdder();
    } else {
      this.openAdder();
    }
  }

  /**
   * Closes the adder component by hiding its content and setting its visibility to false.
   *
   * @return {void} Does not return a value.
   */
  closeAdder(): void {
    setTimeout(() => {
      this.hideContent = true;
    }, 400);
    this.showAdder = false;
  }

  /**
   * Opens the adder by resetting the content visibility and displaying the adder.
   *
   * @return {void} Does not return a value.
   */
  openAdder(): void {
    this.hideContent = false; // Reset immediately on show
    this.showAdder = true;
  }
}
